import _typeof from '@babel/runtime/helpers/typeof';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useGoogleMapsClient } from '@micromag/core/contexts';
import { useDimensionObserver } from '@micromag/core/hooks';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import isObject from 'lodash/isObject';

var styles$2 = [{
  elementType: 'geometry',
  stylers: [{
    color: '#f5f5f5'
  }]
}, {
  elementType: 'labels.icon',
  stylers: [{
    visibility: 'off'
  }]
}, {
  elementType: 'labels.text.fill',
  stylers: [{
    color: '#616161'
  }]
}, {
  elementType: 'labels.text.stroke',
  stylers: [{
    color: '#f5f5f5'
  }]
}, {
  featureType: 'administrative.land_parcel',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'administrative.land_parcel',
  elementType: 'labels.text.fill',
  stylers: [{
    color: '#bdbdbd'
  }]
}, {
  featureType: 'administrative.neighborhood',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'poi',
  elementType: 'geometry',
  stylers: [{
    color: '#eeeeee'
  }]
}, {
  featureType: 'poi',
  elementType: 'labels.text',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'poi',
  elementType: 'labels.text.fill',
  stylers: [{
    color: '#757575'
  }]
}, {
  featureType: 'poi.business',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'poi.park',
  elementType: 'geometry',
  stylers: [{
    color: '#e5e5e5'
  }]
}, {
  featureType: 'poi.park',
  elementType: 'labels.text.fill',
  stylers: [{
    color: '#9e9e9e'
  }]
}, {
  featureType: 'road',
  elementType: 'geometry',
  stylers: [{
    color: '#ffffff'
  }]
}, {
  featureType: 'road',
  elementType: 'labels',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'road',
  elementType: 'labels.icon',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'road.arterial',
  elementType: 'labels',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'road.arterial',
  elementType: 'labels.text.fill',
  stylers: [{
    color: '#757575'
  }]
}, {
  featureType: 'road.highway',
  elementType: 'geometry',
  stylers: [{
    color: '#dadada'
  }]
}, {
  featureType: 'road.highway',
  elementType: 'labels',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'road.highway',
  elementType: 'labels.text.fill',
  stylers: [{
    color: '#616161'
  }]
}, {
  featureType: 'road.local',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'road.local',
  elementType: 'labels.text.fill',
  stylers: [{
    color: '#9e9e9e'
  }]
}, {
  featureType: 'transit',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'transit.line',
  elementType: 'geometry',
  stylers: [{
    color: '#e5e5e5'
  }]
}, {
  featureType: 'transit.station',
  elementType: 'geometry',
  stylers: [{
    color: '#eeeeee'
  }]
}, {
  featureType: 'water',
  elementType: 'geometry',
  stylers: [{
    color: '#c9c9c9'
  }]
}, {
  featureType: 'water',
  elementType: 'labels.text',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'water',
  elementType: 'labels.text.fill',
  stylers: [{
    color: '#9e9e9e'
  }]
}];

var eventsMapping = {
  onClick: ['click', function (map) {
    return map.getCenter();
  }],
  onCenterChanged: ['center_changed', function (map) {
    return map.getCenter();
  }],
  onBoundsChanged: ['bounds_changed', function (map) {
    return map.getBounds();
  }],
  onDragEnd: ['dragend', function (map) {
    return map.getCenter();
  }],
  onTilesLoaded: ['tilesloaded', function () {}]
};
function useGoogleMap(_ref) {
  var zoom = _ref.zoom,
    _ref$maxZoom = _ref.maxZoom,
    maxZoom = _ref$maxZoom === void 0 ? null : _ref$maxZoom,
    _ref$center = _ref.center,
    center = _ref$center === void 0 ? null : _ref$center,
    _ref$withoutStyle = _ref.withoutStyle,
    withoutStyle = _ref$withoutStyle === void 0 ? false : _ref$withoutStyle,
    _ref$fitBounds = _ref.fitBounds,
    fitBounds = _ref$fitBounds === void 0 ? false : _ref$fitBounds,
    _ref$bounds = _ref.bounds,
    bounds = _ref$bounds === void 0 ? null : _ref$bounds,
    events = _ref.events,
    _ref$draggable = _ref.draggable,
    draggable = _ref$draggable === void 0 ? false : _ref$draggable,
    zoomControl = _ref.zoomControl,
    mapTypeControl = _ref.mapTypeControl,
    scaleControl = _ref.scaleControl,
    streetViewControl = _ref.streetViewControl,
    rotateControl = _ref.rotateControl,
    fullscreenControl = _ref.fullscreenControl;
  var client = useGoogleMapsClient();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    ready = _useState2[0],
    setReady = _useState2[1];
  var containerRef = useRef(null);
  var mapRef = useRef(null);
  var _useDimensionObserver = useDimensionObserver(),
    resizeRef = _useDimensionObserver.ref,
    _useDimensionObserver2 = _useDimensionObserver.width,
    width = _useDimensionObserver2 === void 0 ? null : _useDimensionObserver2,
    _useDimensionObserver3 = _useDimensionObserver.height,
    height = _useDimensionObserver3 === void 0 ? null : _useDimensionObserver3;
  useEffect(function () {
    var map = mapRef.current;
    if (map !== null && center !== null) {
      var _ref2 = center || {},
        _ref2$lat = _ref2.lat,
        lat = _ref2$lat === void 0 ? null : _ref2$lat,
        _ref2$lng = _ref2.lng,
        lng = _ref2$lng === void 0 ? null : _ref2$lng;
      map.panTo({
        lat: lat || 0,
        lng: lng || 0
      });
    }
  }, [center]);
  useEffect(function () {
    var map = mapRef.current;
    if (map !== null && zoom !== null) {
      map.setZoom(Math.min(maxZoom, zoom));
    }
  }, [zoom, maxZoom]);
  var updateBounds = useCallback(function (map, newBounds) {
    if (newBounds === null || newBounds.equals(map.getBounds())) {
      return;
    }
    map.fitBounds(newBounds);
    if (map.getZoom() > maxZoom) {
      map.setZoom(maxZoom);
    }
    map.panToBounds(newBounds);
  }, [maxZoom]);
  useEffect(function () {
    var map = mapRef.current;
    if (map !== null && fitBounds) {
      updateBounds(map, bounds);
    }
  }, [updateBounds, bounds, fitBounds, width, height]);
  useEffect(function () {
    var map = mapRef.current;
    if (ready && map !== null) {
      map.setOptions({
        draggable: draggable,
        scrollWheel: draggable,
        disableDoubleClickZoom: !draggable,
        gestureHandling: draggable ? 'cooperative' : 'none'
      });
    }
  }, [ready, draggable]);
  useEffect(function () {
    if (client === null || containerRef.current === null || mapRef.current !== null) {
      return function () {};
    }
    var map = new client.maps.Map(containerRef.current, {
      zoom: zoom,
      center: center,
      styles: !withoutStyle ? styles$2 : null,
      zoomControl: zoomControl,
      mapTypeControl: mapTypeControl,
      scaleControl: scaleControl,
      streetViewControl: streetViewControl,
      rotateControl: rotateControl,
      fullscreenControl: fullscreenControl
    });
    if (fitBounds) {
      updateBounds(map, bounds);
    }
    mapRef.current = map;

    // @TODO unbind events
    if (events !== null) {
      Object.keys(events).forEach(function (eventName) {
        if (events[eventName] !== null) {
          map.addListener(eventsMapping[eventName][0], function () {
            return events[eventName](eventsMapping[eventName][1](map));
          });
        }
      });
    }
    setReady(true);
    return function () {};
  }, [client, setReady]);
  var finalRef = useCallback(function (ref) {
    resizeRef.current = ref;
    containerRef.current = ref;
  }, []);
  return {
    ref: finalRef,
    map: mapRef.current,
    ready: ready,
    loading: !ready
  };
}

var styles$1 = {"container":"micromag-element-map-container","map":"micromag-element-map-map","preventScroll":"micromag-element-map-preventScroll"};

var stopDragEventsPropagation = {
  onTouchMove: function onTouchMove(e) {
    return e.stopPropagation();
  },
  onTouchStart: function onTouchStart(e) {
    return e.stopPropagation();
  },
  onTouchEnd: function onTouchEnd(e) {
    return e.stopPropagation();
  },
  onPointerMove: function onPointerMove(e) {
    return e.stopPropagation();
  },
  onPointerUp: function onPointerUp(e) {
    return e.stopPropagation();
  },
  onPointerDown: function onPointerDown(e) {
    return e.stopPropagation();
  }
};
var propTypes$2 = {
  center: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  }),
  zoom: PropTypes.number,
  maxZoom: PropTypes.number,
  bounds: PropTypes.object,
  // eslint-disable-line
  draggable: PropTypes.bool,
  withoutStyle: PropTypes.bool,
  // Global maps events
  events: PropTypes.object,
  // eslint-disable-line
  fitBounds: PropTypes.bool,
  zoomControl: PropTypes.bool,
  mapTypeControl: PropTypes.bool,
  scaleControl: PropTypes.bool,
  streetViewControl: PropTypes.bool,
  rotateControl: PropTypes.bool,
  fullscreenControl: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
  focusable: PropTypes.bool
};
var defaultProps$2 = {
  center: null,
  zoom: null,
  maxZoom: 16,
  bounds: null,
  events: null,
  fitBounds: false,
  draggable: true,
  withoutStyle: false,
  zoomControl: false,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: false,
  className: null,
  children: null,
  focusable: true
};
var Map$1 = function Map(_ref) {
  var center = _ref.center,
    zoom = _ref.zoom,
    maxZoom = _ref.maxZoom,
    bounds = _ref.bounds,
    draggable = _ref.draggable,
    withoutStyle = _ref.withoutStyle,
    events = _ref.events,
    fitBounds = _ref.fitBounds,
    zoomControl = _ref.zoomControl,
    mapTypeControl = _ref.mapTypeControl,
    scaleControl = _ref.scaleControl,
    streetViewControl = _ref.streetViewControl,
    rotateControl = _ref.rotateControl,
    fullscreenControl = _ref.fullscreenControl,
    className = _ref.className,
    children = _ref.children,
    focusable = _ref.focusable;
  var _useGoogleMap = useGoogleMap({
      zoom: zoom,
      maxZoom: maxZoom,
      bounds: bounds,
      draggable: draggable,
      fitBounds: fitBounds,
      center: center,
      events: events,
      withoutStyle: withoutStyle,
      zoomControl: zoomControl,
      mapTypeControl: mapTypeControl,
      scaleControl: scaleControl,
      streetViewControl: streetViewControl,
      rotateControl: rotateControl,
      fullscreenControl: fullscreenControl
    }),
    map = _useGoogleMap.map,
    mapRef = _useGoogleMap.ref;
  return /*#__PURE__*/React.createElement("div", Object.assign({
    className: classNames([styles$1.container, _defineProperty(_defineProperty({}, className, className !== null), styles$1.preventScroll, !draggable)])
  }, stopDragEventsPropagation), /*#__PURE__*/React.createElement("div", {
    ref: mapRef,
    className: styles$1.map,
    tabIndex: focusable ? '0' : '-1'
  }), React.Children.map(children, function (child) {
    return /*#__PURE__*/React.cloneElement(child, {
      map: map
    });
  }));
};
Map$1.propTypes = propTypes$2;
Map$1.defaultProps = defaultProps$2;

var eventMapping = {
  onClick: 'click',
  onDoubleClick: 'dblclick'
};
function useGoogleMapMarker(map) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref$position = _ref.position,
    position = _ref$position === void 0 ? null : _ref$position,
    _ref$icon = _ref.icon,
    icon = _ref$icon === void 0 ? null : _ref$icon,
    _ref$iconSize = _ref.iconSize,
    iconSize = _ref$iconSize === void 0 ? null : _ref$iconSize,
    events = _ref.events,
    title = _ref.title;
  var client = useGoogleMapsClient();
  var markerRef = useRef(null);
  useEffect(function () {
    var marker = markerRef.current;
    if (marker !== null && position !== null) {
      marker.setPosition(position);
    }
  }, [position]);
  var updateIcon = useCallback(function (marker, newIcon) {
    if (client === null) {
      return;
    }
    if (isObject(newIcon)) {
      var _ref2 = newIcon || {},
        _ref2$url = _ref2.url,
        iconUrl = _ref2$url === void 0 ? null : _ref2$url;
      marker.setIcon({
        url: iconUrl,
        scaledSize: new client.maps.Size(iconSize.width, iconSize.height)
      });
    } else {
      marker.setIcon(newIcon);
    }
  }, [client, iconSize]);
  useEffect(function () {
    var marker = markerRef.current;
    if (marker === null || client === null) {
      return;
    }
    updateIcon(marker, icon);
  }, [client, icon, updateIcon]);
  useEffect(function () {
    if (client === null || map === null || markerRef.current !== null) {
      return function () {};
    }
    var marker = new client.maps.Marker({
      position: position,
      map: map,
      title: title
    });
    updateIcon(marker, icon);
    Object.keys(events).forEach(function (eventName) {
      marker.addListener(eventMapping[eventName], events[eventName]);
    });
    markerRef.current = marker;
    return function () {
      client.maps.event.clearInstanceListeners(marker);
      marker.setMap(null);
    };
  }, [client, map]);
  return markerRef.current;
}

var Pin = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABsAAAArCAYAAACJrvP4AAAC+ElEQVR4AWIgBP7//88DxCGAWssB1pIkCsO1iLG27WCtYG17x3Ywtm3bthlNMLZtz/NrXvStM/cbP/Vln6RSOfq/xqlOi2nO10bxUe26RRKLRdnxiZOnTqVpQO6NOc5AiYSd6O4dRcbAnlJQv4rk/fWt5HzzkbDjEydPHfX0pQr6Q4dcI7RqqZP7Z1z8i/cSLuqop4/+ZCB3iOt214UFVkG9SoikvOijHx1fGAXeqWNm7u9f0Zj2oh+dCoHcOlfkA0oZiB66ZYaBZ53uo/N7pOiWGBqmyF21xKEg24uhQf/mOWJsmaYgYOiiDwfYn5yTRE2X42vNWy/KyKcelZ6PPMCOTzwhEH04ii8ABzMRaNwzj0uXB+8rs4gnAqIPR/HJ4UvgV8wdlICUXOR9+9GHo/jG8enxK+aR+cDI+/ajD0eJ50Vzvv3Yt5h35Acj79ePPhylbetyXuVfA70z9OEoXVy0pbBl40DfGfpwVMy1R1kTRnpBTqM1cZQHh3P2Tiwv1wzynKEPR2HaNI4VNmsQyBcE3bj+UTgYE1kncmCvFQQMXRGpfQsmcre27fNFHVtkFYSeOM459OHcDvw+/mwtzkQ2QOigh64qz7RlrXRmTw1nA4ZOXG+F3z/Iozrkmvm1/80IRD866CkfY1hqeSeOGZnA6EdHJWPaNjdYw/tH0wHRR38q/43Padcx8/75ISUQ9fTRr1IxiUZbR7ZtSulxUk9fsoySZ8+xjxZ1bpkUiDrq6Uv3f/9TXVxk53z3qS+IPHXUq0xMbHuRNWGk77CQp05laiLysoRCbu4vn5cLIk6eOpUN06a52J46zisPRpy8ypaJyOvaMqycrz4sAcInTl5l07RprC3u1qYEDJ+4yraJSI3w5vVmiXO1dYNJPAjYQzo+CDlfvi+A2PGJqyBMG8aegiY1BRg7vgrKYqY53RjUS4Cx4wcGk1isa3zUNTB2/OBgIvVDq5bawNjxg4R96Z07U5T393fCjh8k7E7t2PPEi7rs+Kn0XwF82Z38cjx5pwAAAABJRU5ErkJggg==";

var PinInactive = "data:image/png;base64,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";

var propTypes$1 = {
  // map: PropTypes.object.isRequired, // eslint-disable-line
  map: PropTypes.object,
  // eslint-disable-line
  position: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  }).isRequired,
  type: PropTypes.string,
  // .isRequired,
  events: PropTypes.object,
  // eslint-disable-line
  active: PropTypes.bool,
  title: PropTypes.string,
  image: PropTypes$1.imageMedia,
  iconSize: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number
  })
};
var defaultProps$1 = {
  map: null,
  events: null,
  active: true,
  title: null,
  image: null,
  iconSize: {
    width: 50,
    height: 50
  }
};
var Marker = function Marker(_ref) {
  var map = _ref.map,
    position = _ref.position,
    type = _ref.type,
    events = _ref.events,
    active = _ref.active,
    title = _ref.title,
    image = _ref.image,
    iconSize = _ref.iconSize;
  useGoogleMapMarker(map, {
    position: position,
    type: type,
    events: events,
    title: title,
    icon: image || (active ? Pin : PinInactive),
    iconSize: iconSize
  });
  return null;
};
Marker.propTypes = propTypes$1;
Marker.defaultProps = defaultProps$1;

// import { useEffect } from 'react';
({
  map: PropTypes.object,
  // eslint-disable-line
  coords: PropTypes.arrayOf(PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  })).isRequired,
  events: PropTypes.object // eslint-disable-line
});

({
  map: PropTypes.object,
  // eslint-disable-line
  enabled: PropTypes.bool
});

var styles = {"container":"micromag-element-map-container"};

var propTypes = {
  center: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  }),
  zoom: PropTypes.number,
  draggable: PropTypes.bool,
  markers: PropTypes.arrayOf(PropTypes.object),
  // eslint-disable-line react/forbid-prop-types
  onClickMap: PropTypes.func,
  onClickMarker: PropTypes.func,
  className: PropTypes.string,
  onReady: PropTypes.func,
  onCenterChanged: PropTypes.func,
  onBoundsChanged: PropTypes.func,
  onDrag: PropTypes.func,
  onDragEnd: PropTypes.func,
  withoutStyle: PropTypes.bool,
  fitBounds: PropTypes.bool,
  zoomControl: PropTypes.bool,
  mapTypeControl: PropTypes.bool,
  scaleControl: PropTypes.bool,
  streetViewControl: PropTypes.bool,
  rotateControl: PropTypes.bool,
  fullscreenControl: PropTypes.bool,
  focusable: PropTypes.bool
};
var defaultProps = {
  center: null,
  zoom: null,
  draggable: true,
  markers: [],
  onClickMap: null,
  onClickMarker: null,
  className: null,
  onReady: null,
  onCenterChanged: null,
  onBoundsChanged: null,
  onDrag: null,
  onDragEnd: null,
  withoutStyle: false,
  fitBounds: false,
  zoomControl: false,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: false,
  focusable: true
};
var Map = function Map(_ref) {
  var center = _ref.center,
    zoom = _ref.zoom,
    draggable = _ref.draggable,
    markers = _ref.markers,
    onClickMap = _ref.onClickMap,
    onClickMarker = _ref.onClickMarker,
    className = _ref.className,
    onReady = _ref.onReady,
    onCenterChanged = _ref.onCenterChanged,
    onBoundsChanged = _ref.onBoundsChanged,
    onDrag = _ref.onDrag,
    onDragEnd = _ref.onDragEnd,
    withoutStyle = _ref.withoutStyle,
    fitBounds = _ref.fitBounds,
    zoomControl = _ref.zoomControl,
    mapTypeControl = _ref.mapTypeControl,
    scaleControl = _ref.scaleControl,
    streetViewControl = _ref.streetViewControl,
    rotateControl = _ref.rotateControl,
    fullscreenControl = _ref.fullscreenControl,
    focusable = _ref.focusable;
  var client = useGoogleMapsClient();
  var onClick = useCallback(function (position) {
    if (onClickMap !== null) {
      onClickMap(position);
    }
  }, [onClickMap]);
  var bounds = useMemo(function () {
    var correctMarkers = (markers || []).filter(function (it) {
      return it !== null && (it.geoPosition || null) !== null;
    });
    if (client === null || correctMarkers === null || correctMarkers.length === 0) {
      return null;
    }
    var markersBounds = correctMarkers.reduce(function (newBounds, _ref2) {
      var _ref2$geoPosition = _ref2.geoPosition,
        geoPosition = _ref2$geoPosition === void 0 ? null : _ref2$geoPosition;
      var _ref3 = geoPosition || {},
        _ref3$lat = _ref3.lat,
        lat = _ref3$lat === void 0 ? null : _ref3$lat,
        _ref3$lng = _ref3.lng,
        lng = _ref3$lng === void 0 ? null : _ref3$lng;
      if (lat !== null && lng !== null) {
        newBounds.extend(new client.maps.LatLng(lat, lng));
      }
      return newBounds;
    }, new client.maps.LatLngBounds());
    return markersBounds;
  }, [client, markers]);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    loaded = _useState2[0],
    setLoaded = _useState2[1];
  useEffect(function () {
    if (client !== null && loaded) {
      if (onReady !== null) {
        onReady(client);
      }
    }
  }, [client, loaded, onReady]);
  var onTilesLoaded = useCallback(function () {
    return setLoaded(true);
  }, [setLoaded]);
  var events = useMemo(function () {
    return {
      onClick: onClick,
      onCenterChanged: onCenterChanged,
      onBoundsChanged: onBoundsChanged,
      onDrag: onDrag,
      onDragEnd: onDragEnd,
      onTilesLoaded: onTilesLoaded
    };
  }, [onClick, onCenterChanged, onBoundsChanged, onDrag, onDragEnd, onTilesLoaded]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty({}, className, className !== null)])
  }, /*#__PURE__*/React.createElement(Map$1, {
    center: center,
    zoom: zoom,
    withoutStyle: withoutStyle,
    bounds: bounds,
    draggable: draggable,
    events: events,
    fitBounds: fitBounds,
    zoomControl: zoomControl,
    mapTypeControl: mapTypeControl,
    scaleControl: scaleControl,
    streetViewControl: streetViewControl,
    rotateControl: rotateControl,
    fullscreenControl: fullscreenControl,
    focusable: focusable
  }, markers !== null ? markers.map(function (_ref5, index) {
    var _ref5$active = _ref5.active,
      active = _ref5$active === void 0 ? true : _ref5$active,
      _ref5$geoPosition = _ref5.geoPosition,
      geoPosition = _ref5$geoPosition === void 0 ? null : _ref5$geoPosition,
      _ref5$image = _ref5.image,
      image = _ref5$image === void 0 ? null : _ref5$image,
      _ref5$title = _ref5.title,
      title = _ref5$title === void 0 ? null : _ref5$title;
    return geoPosition !== null && (geoPosition.lat || null) !== null && (geoPosition.lng || null) !== null ? /*#__PURE__*/React.createElement(Marker, {
      key: "marker-".concat(index),
      active: active,
      title: title !== null && _typeof(title.body) ? title.body : null,
      image: image,
      position: geoPosition,
      events: {
        onClick: onClickMarker !== null ? function (e) {
          return onClickMarker(e, index);
        } : function () {
          return console.log(index);
        } // eslint-disable-line no-console
      }
    }) : /*#__PURE__*/React.createElement("div", null);
  }) : null));
};
Map.propTypes = propTypes;
Map.defaultProps = defaultProps;

export { Pin, Map as default };
